import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ShellDomainProvider, UserInfoService } from '../standalone-packages/ecap3/core';
import {
    BehaviorSubject,
    Observable,
    Subject,
    catchError,
    concatMap,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    of,
    scan,
    switchMap,
    take,
    tap
} from 'rxjs';
import { QueryHandlerResponse } from '../models/responseModels/QueryHandlerResponse';
import { IpexUserInformation } from '../models/domain/IpexUserInformation';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { MarkForQualityCheckCommand } from '../models/commandModels/MarkForQualityCheckCommand';
import { IpexCommandResponse } from '../models/responseModels/IpexCommandResponse';
import { UpdateIpexUserCommand } from '../models/commandModels/UpdateIpexUserCommand';
import { UpdateCaseDetailsPanelStateCommand } from '../models/commandModels/UpdateCaseDetailsPanelStateCommand';
import { CaseDetailsPanelState, DEFAULT_CASE_DETAILS_PANEL_STATE } from '../models/domain/CaseDetailsPanelState';
import { UrlResolverService } from './url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class IpexUserInformationService {
    private readonly urlResolver = inject(UrlResolverService);
    private readonly pcxCommandUrl: string = this.urlResolver.getPcxCommandUrl();
    private readonly pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    private readonly getIpexUserInformationUrl: string = `${this.pcxQueryUrl}GetIpexUserInformation` as const;
    private readonly markForQualityCheckUrl: string = `${this.pcxCommandUrl}MarkForQualityCheck` as const;
    private readonly updateIpexUserUrl: string = `${this.pcxCommandUrl}UpdateIpexUser` as const;
    private readonly updateCaseDetailsPanelStateUrl: string =
        `${this.pcxCommandUrl}UpdateCaseDetailsPanelState` as const;

    private currentUserInformationSubject = new BehaviorSubject<IpexUserInformation | null>(null);
    public currentUserInformation$ = this.currentUserInformationSubject.asObservable();
    public currentUserInformation: IpexUserInformation | null = null;

    // Debounced so rapid toggles coalesce into one request; concatMap sends requests one at a time, in order.
    private readonly panelStateUpdateRequest = new Subject<Partial<CaseDetailsPanelState>>();

    constructor(
        private httpClient: HttpClient,
        private userInfoService: UserInfoService
    ) {
        this.listenForPanelStateUpdateRequests();
    }

    public getIpexUserInformation(userIds: string[]): Observable<QueryHandlerResponse<null, IpexUserInformation[]>> {
        const payload = {
            UserIds: userIds
        };
        return this.httpClient.post<QueryHandlerResponse<null, IpexUserInformation[]>>(
            this.getIpexUserInformationUrl,
            payload,
            {
                headers: SCConfig.HttpCommonHeader,
                withCredentials: true
            }
        );
    }

    public getCurrentLoggedInUserInformation(): Observable<IpexUserInformation | null> {
        return this.userInfoService.currentUser.pipe(
            distinctUntilChanged(),
            mergeMap(currentUser => {
                if (currentUser === 'DEFAULT_MESSAGE' || currentUser?.UserId === '') {
                    return of(null);
                } else {
                    return this.getIpexUserInformation([currentUser.UserId]).pipe(
                        map(response => {
                            if (response && response.Results.length > 0) {
                                return response.Results[0];
                            }
                            return null;
                        })
                    );
                }
            }),
            tap(userInfo => {
                if (userInfo !== null) {
                    // Field first: subscribers are notified synchronously and may read it.
                    this.currentUserInformation = userInfo;
                    this.currentUserInformationSubject.next(userInfo);
                }
            })
        );
    }

    public markForQualityCheck(payload: MarkForQualityCheckCommand): Observable<IpexCommandResponse<void>> {
        return this.httpClient.post<IpexCommandResponse<void>>(this.markForQualityCheckUrl, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    public updateIpexUserInformation(payload: UpdateIpexUserCommand): Observable<IpexCommandResponse<boolean>> {
        return this.httpClient.post<IpexCommandResponse<boolean>>(this.updateIpexUserUrl, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    private listenForPanelStateUpdateRequests(): void {
        this.panelStateUpdateRequest
            .pipe(
                // Accumulate so a debounced-away partial (e.g. a different field toggled milliseconds later)
                // isn't lost — only the redundant network call is skipped, not the change itself.
                scan(
                    (accumulated, partialState) => ({ ...accumulated, ...partialState }),
                    {} as Partial<CaseDetailsPanelState>
                ),
                debounceTime(300),
                // Wait for the user record: merging without it would send DEFAULT_* over the other saved flags.
                switchMap(accumulatedPartialState =>
                    this.currentUserInformation$.pipe(
                        filter(userInfo => !!userInfo),
                        take(1),
                        map(() => accumulatedPartialState)
                    )
                ),
                // concatMap, not switchMap: a cancelled request can still land server-side after a newer one.
                concatMap(accumulatedPartialState => this.postCaseDetailsPanelState(accumulatedPartialState))
            )
            .subscribe();
    }

    private postCaseDetailsPanelState(
        accumulatedPartialState: Partial<CaseDetailsPanelState>
    ): Observable<IpexCommandResponse<boolean> | null> {
        // Merged at send time, so a queued request builds on the state the previous one confirmed.
        const mergedState: CaseDetailsPanelState = {
            ...DEFAULT_CASE_DETAILS_PANEL_STATE,
            ...this.currentUserInformation?.CaseDetailsPanelState,
            ...accumulatedPartialState
        };
        const payload: UpdateCaseDetailsPanelStateCommand = { CaseDetailsPanelState: mergedState };

        return this.httpClient
            .post<IpexCommandResponse<boolean>>(this.updateCaseDetailsPanelStateUrl, payload, {
                headers: SCConfig.HttpCommonHeader,
                withCredentials: true
            })
            .pipe(
                tap(res => {
                    if (res.StatusCode === 0 && res.Result === true && this.currentUserInformation) {
                        this.currentUserInformation = {
                            ...this.currentUserInformation,
                            CaseDetailsPanelState: mergedState
                        };
                        this.currentUserInformationSubject.next(this.currentUserInformation);
                    }
                }),
                catchError(() => of(null))
            );
    }

    // Fire-and-forget: queued through the concatMap pipeline above, so requests land in order.
    public updateCaseDetailsPanelState(partialState: Partial<CaseDetailsPanelState>): void {
        this.panelStateUpdateRequest.next(partialState);
    }
}
